import { FontWeight } from '../types'

export interface ThemeFont {
  family: string
  category: 'sans-serif' | 'serif' | 'display' | 'monospace' | 'handwriting'
  weights: FontWeight[]
  googleName: string // URL-encoded name for Google Fonts API
}

// Extended font list including display/decorative fonts for themes
export const THEME_FONTS: ThemeFont[] = [
  // Sans-serif (Body/UI)
  {
    family: 'System UI',
    category: 'sans-serif',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold', 'black'],
    googleName: '',
  },
  {
    family: 'Inter',
    category: 'sans-serif',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold', 'black'],
    googleName: 'Inter',
  },
  {
    family: 'Roboto',
    category: 'sans-serif',
    weights: ['normal', 'medium', 'bold', 'black'],
    googleName: 'Roboto',
  },
  {
    family: 'Open Sans',
    category: 'sans-serif',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold'],
    googleName: 'Open+Sans',
  },
  {
    family: 'Poppins',
    category: 'sans-serif',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold', 'black'],
    googleName: 'Poppins',
  },
  {
    family: 'Nunito',
    category: 'sans-serif',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold', 'black'],
    googleName: 'Nunito',
  },
  {
    family: 'Work Sans',
    category: 'sans-serif',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold', 'black'],
    googleName: 'Work+Sans',
  },
  {
    family: 'DM Sans',
    category: 'sans-serif',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold'],
    googleName: 'DM+Sans',
  },
  // Serif (Elegant)
  {
    family: 'Lora',
    category: 'serif',
    weights: ['normal', 'medium', 'semibold', 'bold'],
    googleName: 'Lora',
  },
  {
    family: 'Merriweather',
    category: 'serif',
    weights: ['normal', 'bold', 'black'],
    googleName: 'Merriweather',
  },
  {
    family: 'Crimson Pro',
    category: 'serif',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold'],
    googleName: 'Crimson+Pro',
  },
  {
    family: 'EB Garamond',
    category: 'serif',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold'],
    googleName: 'EB+Garamond',
  },
  {
    family: 'Cormorant Garamond',
    category: 'serif',
    weights: ['normal', 'medium', 'semibold', 'bold'],
    googleName: 'Cormorant+Garamond',
  },
  {
    family: 'Libre Baskerville',
    category: 'serif',
    weights: ['normal', 'bold'],
    googleName: 'Libre+Baskerville',
  },
  {
    family: 'Source Serif 4',
    category: 'serif',
    weights: ['normal', 'semibold', 'bold', 'black'],
    googleName: 'Source+Serif+4',
  },
  // Display (Headlines)
  {
    family: 'Montserrat',
    category: 'display',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold', 'black'],
    googleName: 'Montserrat',
  },
  {
    family: 'Playfair Display',
    category: 'display',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold', 'black'],
    googleName: 'Playfair+Display',
  },
  {
    family: 'Space Grotesk',
    category: 'display',
    weights: ['normal', 'medium', 'semibold', 'bold'],
    googleName: 'Space+Grotesk',
  },
  {
    family: 'Sora',
    category: 'display',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold'],
    googleName: 'Sora',
  },
  {
    family: 'Bebas Neue',
    category: 'display',
    weights: ['normal'],
    googleName: 'Bebas+Neue',
  },
  {
    family: 'Oswald',
    category: 'display',
    weights: ['normal', 'medium', 'semibold', 'bold'],
    googleName: 'Oswald',
  },
  // Horror/Spooky Display Fonts
  {
    family: 'Nosifer',
    category: 'display',
    weights: ['normal'],
    googleName: 'Nosifer',
  },
  {
    family: 'Creepster',
    category: 'display',
    weights: ['normal'],
    googleName: 'Creepster',
  },
  {
    family: 'Butcherman',
    category: 'display',
    weights: ['normal'],
    googleName: 'Butcherman',
  },
  {
    family: 'Eater',
    category: 'display',
    weights: ['normal'],
    googleName: 'Eater',
  },
  {
    family: 'Metal Mania',
    category: 'display',
    weights: ['normal'],
    googleName: 'Metal+Mania',
  },
  // Elegant Script/Handwriting
  {
    family: 'Great Vibes',
    category: 'handwriting',
    weights: ['normal'],
    googleName: 'Great+Vibes',
  },
  {
    family: 'Pinyon Script',
    category: 'handwriting',
    weights: ['normal'],
    googleName: 'Pinyon+Script',
  },
  {
    family: 'Alex Brush',
    category: 'handwriting',
    weights: ['normal'],
    googleName: 'Alex+Brush',
  },
  {
    family: 'Sacramento',
    category: 'handwriting',
    weights: ['normal'],
    googleName: 'Sacramento',
  },
  {
    family: 'Tangerine',
    category: 'handwriting',
    weights: ['normal', 'bold'],
    googleName: 'Tangerine',
  },
  // Monospace
  {
    family: 'JetBrains Mono',
    category: 'monospace',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold'],
    googleName: 'JetBrains+Mono',
  },
  {
    family: 'Fira Code',
    category: 'monospace',
    weights: ['normal', 'medium', 'semibold', 'bold'],
    googleName: 'Fira+Code',
  },
]

// Get font by family name
export function getFontByFamily(family: string): ThemeFont | undefined {
  return THEME_FONTS.find((f) => f.family === family)
}

// Get fonts by category
export function getFontsByCategory(category: ThemeFont['category']): ThemeFont[] {
  return THEME_FONTS.filter((f) => f.category === category)
}

// Get display fonts (for headlines)
export function getDisplayFonts(): ThemeFont[] {
  return THEME_FONTS.filter((f) =>
    f.category === 'display' || f.category === 'serif' || f.category === 'handwriting'
  )
}

// Get body fonts (readable for long text)
export function getBodyFonts(): ThemeFont[] {
  return THEME_FONTS.filter((f) =>
    f.category === 'sans-serif' || f.category === 'serif'
  )
}

// Font weight to numeric value mapping
export const FONT_WEIGHT_VALUES: Record<FontWeight, number> = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  black: 900,
}

// Font weight display labels
export const FONT_WEIGHT_LABELS: Record<FontWeight, string> = {
  normal: 'Normal',
  medium: 'Medium',
  semibold: 'Semibold',
  bold: 'Bold',
  extrabold: 'Extra Bold',
  black: 'Black',
}

// Check if a weight is available for a font
export function isWeightAvailable(family: string, weight: FontWeight): boolean {
  const font = getFontByFamily(family)
  if (!font) return false
  return font.weights.includes(weight)
}

// Get CSS font-family value with fallbacks
export function getFontFamilyValue(family: string): string {
  if (family === 'System UI') {
    return "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  }
  const font = getFontByFamily(family)
  if (!font) return `'${family}', sans-serif`

  const fallback = font.category === 'monospace'
    ? 'ui-monospace, monospace'
    : font.category === 'serif'
    ? 'Georgia, serif'
    : font.category === 'handwriting'
    ? 'cursive'
    : 'system-ui, sans-serif'

  return `'${family}', ${fallback}`
}

// Build Google Fonts URL for multiple fonts
export function buildGoogleFontsUrl(families: string[]): string | null {
  const fontsToLoad = families
    .map((family) => getFontByFamily(family))
    .filter((f): f is ThemeFont => f !== undefined && f.googleName !== '')

  if (fontsToLoad.length === 0) return null

  const familyParams = fontsToLoad.map((font) => {
    const weights = font.weights.map((w) => FONT_WEIGHT_VALUES[w]).join(';')
    return `family=${font.googleName}:wght@${weights}`
  })

  return `https://fonts.googleapis.com/css2?${familyParams.join('&')}&display=swap`
}
